import { useState, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Path } from '@/routes/paths'
import { useRouteNavigate } from '@features/navigation/hooks/useRouteNavigate'
import { getErrorMessage } from '@features/shared/api/client'
import { DropdownMenu, type MenuSection } from '@features/shared/components/DropdownMenu'
import { IconButton } from '@features/shared/components/IconButton'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import { useDebouncedValue } from '@features/shared/hooks/useDebouncedValue'
import { EditIcon, KebabIcon, PlusIcon, TrashIcon } from '@features/shared/icons/icons'
import {
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  menuIconSlotClass,
  menuIconSlotsLeadClass,
} from '@features/sidebar/components/menuIconSlots'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'
import {
  CHAT_THREAD_TITLE_MAX_LENGTH,
  type ChatFolderSummary,
  type ChatThreadSummary,
} from '../api/contracts'
import { useChatFolderThreads, useChatFolders } from '../hooks/useChatFolders'
import { useChatThreadSearch, useChatThreads } from '../hooks/useChatThreads'
import { unfiledThreads } from '../logic/unfiledThreads'
import ChatFolderSection, { NewChatFolderRow } from './ChatFolderSection'
import {
  NAME_INPUT_CLASS,
  NOTE_CLASS,
  ROW_CLASS,
  ROW_STATE_CLASS,
  SIDEBAR_DROPDOWN_Z_INDEX,
} from './chatHistoryRowStyles'

const SEARCH_DEBOUNCE_MS = 250

interface ChatHistoryListProps {
  /** The drawer's search band (ModuleDrawer): blank lists every conversation. */
  searchQuery: string
  /** Called after navigating somewhere, so an unpinned drawer can close. */
  onSelect?: () => void
  /** Opens in the host panel instead of changing the page's route. */
  onOpenThread?: (threadId: string) => void
  onNewChat?: () => void
  activeThreadId?: string | null
  onThreadDeleted?: (threadId: string) => void
}

interface ThreadRowProps {
  onOpen?: () => void
  threadId: string
  title: string
  snippet?: string | null
  isActive: boolean
  onSelect?: () => void
  onRename: (title: string) => Promise<unknown>
  onDelete: () => Promise<unknown>
  /** The caller's folders, offered in the menu's Move to folder section. */
  folders: ChatFolderSummary[]
  /** The folder the conversation is in; null when it's in none. */
  folderId: string | null
  onMove: (folderId: string | null) => Promise<unknown>
}

function ThreadRow({
  onOpen,
  threadId,
  title,
  snippet,
  isActive,
  onSelect,
  onRename,
  onDelete,
  folders,
  folderId,
  onMove,
}: ThreadRowProps) {
  const [mode, setMode] = useState<'view' | 'rename' | 'confirm-delete'>('view')
  const [draftTitle, setDraftTitle] = useState(title)
  const [menuOpen, setMenuOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setIsBusy(true)
    setError(null)
    try {
      await action()
      setMode('view')
    } catch (err) {
      setError(getErrorMessage(err, failure))
    } finally {
      setIsBusy(false)
    }
  }

  const submitRename = () => {
    if (isBusy) return
    const next = draftTitle.trim()
    if (!next || next === title) {
      setMode('view')
      return
    }
    void run(() => onRename(next), "This conversation couldn't be renamed.")
  }

  const menuSections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'rename-chat-thread',
          label: 'Rename',
          icon: <EditIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => {
            setDraftTitle(title)
            setError(null)
            setMode('rename')
          },
        },
        {
          type: 'action',
          id: 'delete-chat-thread',
          label: 'Delete',
          tone: 'danger',
          icon: <TrashIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => {
            setError(null)
            setMode('confirm-delete')
          },
        },
      ],
    },
  ]
  if (folders.length > 0) {
    const moveTo = (target: string | null) => {
      if (target === folderId) return
      setMoveError(null)
      onMove(target).catch((err: unknown) =>
        setMoveError(getErrorMessage(err, "This conversation couldn't be moved."))
      )
    }
    menuSections.push({
      label: 'Move to folder',
      items: [
        ...folders.map((folder) => ({
          type: 'option' as const,
          id: `move-chat-thread-${folder.folder_id}`,
          label: folder.name,
          selected: folder.folder_id === folderId,
          onSelect: () => moveTo(folder.folder_id),
        })),
        {
          type: 'option',
          id: 'move-chat-thread-none',
          label: 'No folder',
          selected: folderId === null,
          onSelect: () => moveTo(null),
        },
      ],
    })
  }

  if (mode === 'rename') {
    return (
      <div className="px-1 py-1">
        <input
          autoFocus
          type="text"
          aria-label="Conversation name"
          value={draftTitle}
          maxLength={CHAT_THREAD_TITLE_MAX_LENGTH}
          disabled={isBusy}
          onChange={(event) => setDraftTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submitRename()
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              setMode('view')
            }
          }}
          onBlur={submitRename}
          className={NAME_INPUT_CLASS}
        />
        {error && <p className="px-1 pt-1 text-xs text-danger">{error}</p>}
      </div>
    )
  }

  if (mode === 'confirm-delete') {
    // Confirmed inline rather than in a modal: an unpinned drawer unmounts
    // its content as soon as the pointer leaves it.
    return (
      <div className="rounded-md px-2 py-2 text-sm ring-1 ring-[color:var(--sidebar-submenu-border)]">
        <p className="text-[color:var(--sidebar-submenu-text-data)]">
          Delete{' '}
          <span className="font-medium text-[color:var(--sidebar-submenu-text-default)]">
            {title}
          </span>
          ? This can't be undone.
        </p>
        {error && <p className="pt-1 text-xs text-danger">{error}</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setMode('view')}
            disabled={isBusy}
            className="rounded-md px-2 py-1 text-xs font-medium text-[color:var(--sidebar-submenu-text-data)] hover:bg-[color:var(--sidebar-item-hover-bg)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void run(onDelete, "This conversation couldn't be deleted.")}
            disabled={isBusy}
            className="rounded-md bg-[color:var(--action-danger-bg)] px-2 py-1 text-xs font-medium text-[color:var(--action-danger-text)] hover:bg-[color:var(--action-danger-bg-hover)] disabled:opacity-60"
          >
            {isBusy ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    )
  }

  const handleRouteClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!event.ctrlKey && !event.metaKey) onSelect?.()
  }

  const content = (
    <>
      <FadeTruncatedText>{title}</FadeTruncatedText>
      {snippet && (
        <FadeTruncatedText className="text-xs font-normal text-[color:var(--sidebar-submenu-text-muted)]">
          {snippet}
        </FadeTruncatedText>
      )}
    </>
  )
  const linkClass =
    "flex min-w-0 flex-1 flex-col justify-center self-stretch py-1 text-left after:absolute after:inset-0 after:rounded-md after:content-['']"

  const row = (
    <div className={`${ROW_CLASS} ${isActive ? ROW_STATE_CLASS.active : ROW_STATE_CLASS.idle}`}>
      {onOpen ? (
        <button
          type="button"
          // eslint-disable-next-line no-restricted-syntax -- Full text of the truncated conversation title.
          title={title}
          aria-current={isActive ? 'true' : undefined}
          onClick={onOpen}
          className={linkClass}
        >
          {content}
        </button>
      ) : (
        <Link
          to={Path.aiChatThread(threadId)}
          // The full text of the truncated title, on the link (whose overlay
          // covers the row) so the row's own controls show only their tooltips.
          title={title}
          aria-current={isActive ? 'page' : undefined}
          onClick={handleRouteClick}
          className={linkClass}
        >
          {content}
        </Link>
      )}
      <span
        className={`pointer-events-none relative z-10 ml-auto h-6 ${menuIconSlotsLeadClass(
          menuOpen
        )} ${MENU_ICON_SLOTS_CLASS}`}
      >
        <span className={menuIconSlotClass(menuOpen ? 'shown' : 'collapsed')}>
          <DropdownMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            sections={menuSections}
            align="right"
            placement="right-start"
            sideOffsetPx={6}
            className="min-w-40"
            portalZIndex={SIDEBAR_DROPDOWN_Z_INDEX}
            trigger={
              <IconButton
                label="Conversation options"
                tooltip={menuOpen ? null : undefined}
                className={`pointer-events-auto ${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
              >
                <KebabIcon size={14} aria-hidden="true" />
              </IconButton>
            }
          />
        </span>
      </span>
    </div>
  )

  if (!moveError) return row
  return (
    <div>
      {row}
      <p className="px-2 pb-1 text-xs text-danger">{moveError}</p>
    </div>
  )
}

interface FolderThreadsProps {
  folder: ChatFolderSummary
  renderThread: (thread: ChatThreadSummary) => ReactNode
  onRename: (name: string) => Promise<unknown>
  onDelete: () => Promise<unknown>
}

/** A folder's section with every conversation filed in it, from the folder's
 * own query: the history list holds only the most recent conversations. */
function FolderThreads({ folder, renderThread, onRename, onDelete }: FolderThreadsProps) {
  const { threads, isLoading, isError } = useChatFolderThreads(folder.folder_id)
  return (
    <ChatFolderSection
      name={folder.name}
      threadCount={threads.length}
      isLoading={isLoading}
      isError={isError}
      onRename={onRename}
      onDelete={onDelete}
    >
      {threads.map(renderThread)}
    </ChatFolderSection>
  )
}

/**
 * The AI Assistant's drawer: the signed-in user's own conversations, newest
 * activity first, with new chat, rename and delete, searched from the
 * drawer's search band (typo-tolerant, over titles and messages). The
 * user's folders come first, each with its conversations; the ones in no
 * folder follow.
 */
export default function ChatHistoryList({
  searchQuery,
  onSelect,
  onOpenThread,
  onNewChat,
  activeThreadId,
  onThreadDeleted,
}: ChatHistoryListProps) {
  const navigate = useRouteNavigate()
  const location = useLocation()
  const { threads, isLoading, isError, retryLoad, renameThread, deleteThread } = useChatThreads()
  const { folders, createFolder, renameFolder, deleteFolder, moveThread } = useChatFolders()
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const search = useChatThreadSearch(debouncedQuery)
  const isSearching = searchQuery.trim().length > 0

  const isOpen = (threadId: string) =>
    onOpenThread ? activeThreadId === threadId : location.pathname === Path.aiChatThread(threadId)
  // Search hits carry no folder; they're the listed threads (or older ones, unfiled here).
  const folderOf = new Map(threads.map((thread) => [thread.thread_id, thread.folder_id ?? null]))

  const rowActions = (threadId: string, folderId: string | null) => ({
    onRename: (title: string) => renameThread({ threadId, title }),
    onDelete: async () => {
      const wasOpen = isOpen(threadId)
      await deleteThread(threadId)
      onThreadDeleted?.(threadId)
      if (wasOpen && !onOpenThread) navigate(Path.aiChat, { replace: true })
    },
    folders,
    folderId,
    onMove: (folderId: string | null) => moveThread({ threadId, folderId }),
  })

  const renderThread = (thread: ChatThreadSummary) => (
    <ThreadRow
      key={thread.thread_id}
      threadId={thread.thread_id}
      title={thread.title}
      isActive={isOpen(thread.thread_id)}
      onSelect={onSelect}
      onOpen={onOpenThread ? () => onOpenThread(thread.thread_id) : undefined}
      {...rowActions(thread.thread_id, thread.folder_id ?? null)}
    />
  )
  const unfiled = unfiledThreads(threads, folders)

  return (
    <div className="flex min-w-full flex-col gap-0.5">
      <button
        type="button"
        onClick={() => {
          if (onNewChat) onNewChat()
          else {
            navigate(Path.aiChat)
            onSelect?.()
          }
        }}
        className={`${ROW_CLASS} gap-2 font-medium ${ROW_STATE_CLASS.idle}`}
      >
        <PlusIcon size={14} className="shrink-0" aria-hidden="true" />
        New chat
      </button>
      <NewChatFolderRow onCreate={createFolder} />

      <div
        role="separator"
        className="my-1 border-t border-[color:var(--sidebar-settings-divider)]"
      />

      {isSearching ? (
        search.error ? (
          <p className={NOTE_CLASS}>Search isn't available right now.</p>
        ) : search.results.length > 0 ? (
          search.results.map((result) => (
            <ThreadRow
              key={result.thread_id}
              threadId={result.thread_id}
              title={result.title}
              snippet={result.snippet}
              isActive={isOpen(result.thread_id)}
              onSelect={onSelect}
              onOpen={onOpenThread ? () => onOpenThread(result.thread_id) : undefined}
              {...rowActions(result.thread_id, folderOf.get(result.thread_id) ?? null)}
            />
          ))
        ) : (
          <p className={NOTE_CLASS}>
            {search.isSearching || searchQuery !== debouncedQuery
              ? 'Searching...'
              : 'No conversations match.'}
          </p>
        )
      ) : isLoading ? null : isError ? (
        <div className="px-2 py-2 text-sm">
          <p className="text-danger">Conversations couldn't be loaded.</p>
          <button
            type="button"
            onClick={() => void retryLoad()}
            className="mt-1 text-[color:var(--accent-info)] hover:underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {folders.map((folder) => (
            <FolderThreads
              key={folder.folder_id}
              folder={folder}
              renderThread={renderThread}
              onRename={(name) => renameFolder({ folderId: folder.folder_id, name })}
              onDelete={() => deleteFolder(folder.folder_id)}
            />
          ))}
          {threads.length === 0 ? (
            <p className={NOTE_CLASS}>No conversations yet.</p>
          ) : (
            unfiled.map(renderThread)
          )}
        </>
      )}
    </div>
  )
}
